import { useState } from 'react';
import { canTransferUnionLeadership, leadershipTransferConfirmation } from '../../union-member-management/domain/unionLeadershipTransfer.js';
import { usePlatformUnionManagement } from '../hooks/usePlatformUnionManagement.js';

const ROLE_LABELS = {
  leader: '주딱',
  deputy: '파딱',
  member: '멤버',
};

const COMMUNITY_TYPE_LABELS = {
  gallery: '갤러리(갤길)',
  channel: '채널(챈길)',
};

const COMMUNITY_TYPE_SHORT_LABELS = {
  gallery: '갤',
  channel: '챈',
};

function formatJoinedAt(value) {
  if (!value) return '확인 불가';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(value));
  } catch {
    return '확인 불가';
  }
}

export function PlatformUnionManagementBoard({ mobile = false }) {
  const state = usePlatformUnionManagement();
  const [pendingCommunityType, setPendingCommunityType] = useState('');

  const currentCommunityType = state.selectedUnion?.communityType || '';
  const selectedCommunityType = pendingCommunityType || currentCommunityType;

  const handleCommunityTypeSave = async () => {
    if (!selectedCommunityType || selectedCommunityType === currentCommunityType) return;
    const success = await state.setCommunityType(selectedCommunityType);
    if (success) setPendingCommunityType('');
  };

  return (
    <section className={`platform-union-management ${mobile ? 'mobile' : 'desktop'}`} aria-label="유니온 리더십·커뮤니티 유형 관리">
      <header className="platform-union-management-heading">
        <div>
          <h2>유니온 관리</h2>
          <p>플랫폼 관리자가 유니온의 주딱·파딱과 커뮤니티 유형(갤러리/채널)을 직접 조정합니다.</p>
        </div>
      </header>

      <div className="platform-union-management-controls">
        <label>
          <span>유니온</span>
          <select
            value={state.selectedUnionId}
            disabled={state.loadingUnions}
            onChange={(event) => {
              state.selectUnion(event.target.value);
              setPendingCommunityType('');
            }}
          >
            {state.unions.length
              ? state.unions.map((union) => (
                <option key={union.unionId} value={union.unionId}>
                  [{COMMUNITY_TYPE_SHORT_LABELS[union.communityType] || '?'}] {union.unionName}{union.guildId ? ` · ${union.guildId}` : ''}
                </option>
              ))
              : <option value="">관리 가능한 유니온 없음</option>}
          </select>
        </label>

        <div className="platform-union-management-community-type">
          <span>커뮤니티 유형</span>
          {state.selectedUnionId ? (
            <strong className="platform-union-management-community-type-current">
              현재: {COMMUNITY_TYPE_LABELS[currentCommunityType] || '확인 불가'}
            </strong>
          ) : null}
          <label>
            <input
              type="radio"
              name="platform-union-community-type"
              value="gallery"
              disabled={!state.selectedUnionId}
              checked={selectedCommunityType === 'gallery'}
              onChange={() => setPendingCommunityType('gallery')}
            />
            갤러리(갤길)
          </label>
          <label>
            <input
              type="radio"
              name="platform-union-community-type"
              value="channel"
              disabled={!state.selectedUnionId}
              checked={selectedCommunityType === 'channel'}
              onChange={() => setPendingCommunityType('channel')}
            />
            채널(챈길)
          </label>
          <button
            type="button"
            disabled={!state.selectedUnionId || !selectedCommunityType || selectedCommunityType === currentCommunityType}
            onClick={handleCommunityTypeSave}
          >
            변경 저장
          </button>
        </div>
      </div>

      {state.loadingUnions || state.loadingMembers ? (
        <div className="platform-union-management-loading" role="status">
          <span aria-hidden="true" />
          <p>유니온 멤버 정보를 불러오고 있습니다.</p>
        </div>
      ) : !state.selectedUnionId ? (
        <div className="platform-union-management-empty">
          <strong>관리할 활성 유니온이 없습니다.</strong>
        </div>
      ) : state.members.length === 0 ? (
        <div className="platform-union-management-empty">
          <strong>표시할 활성 멤버가 없습니다.</strong>
        </div>
      ) : (
        <div className="platform-union-management-list">
          {state.members.map((member) => {
            const isWorking = state.workingMembershipId === member.membershipId;
            const nextRole = member.role === 'deputy' ? 'member' : 'deputy';
            const promotionBlocked = nextRole === 'deputy' && !member.guildMatches;
            const canTransferLeadership = canTransferUnionLeadership({
              viewerRole: 'leader',
              memberRole: member.role,
              isSelf: false,
              guildMatches: member.guildMatches,
            });

            return (
              <article className={`platform-union-management-card role-${member.role}`} key={member.membershipId}>
                <div className="platform-union-management-card-head">
                  <span>{ROLE_LABELS[member.role] || member.role}</span>
                  <h3>{member.nickname || '닉네임 확인 불가'}</h3>
                </div>
                <dl>
                  <div>
                    <dt>가입일</dt>
                    <dd>{formatJoinedAt(member.joinedAt)}</dd>
                  </div>
                  <div>
                    <dt>게임 소속</dt>
                    <dd className={member.guildMatches ? 'matched' : 'mismatch'}>{member.guildMatches ? '일치' : '불일치'}</dd>
                  </div>
                </dl>
                <div className="platform-union-management-actions">
                  {canTransferLeadership ? (
                    <button
                      type="button"
                      className="transfer-leader"
                      disabled={Boolean(state.workingMembershipId)}
                      onClick={() => {
                        if (window.confirm(leadershipTransferConfirmation(member.nickname))) {
                          state.transferLeadership(member.membershipId);
                        }
                      }}
                    >
                      {isWorking ? '이전 중…' : '주딱 이전'}
                    </button>
                  ) : null}

                  {member.role !== 'leader' ? (
                    <button
                      type="button"
                      className="role-change"
                      disabled={Boolean(state.workingMembershipId) || promotionBlocked}
                      title={promotionBlocked ? '게임 프로필 소속이 일치해야 파딱으로 지정할 수 있습니다.' : ''}
                      onClick={() => state.setMemberRole({ membershipId: member.membershipId, role: nextRole })}
                    >
                      {isWorking ? '처리 중…' : nextRole === 'deputy' ? '파딱 지정' : '파딱 해제'}
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {state.notice.message ? (
        <p className={`platform-union-management-notice ${state.notice.type}`} role="status">
          {state.notice.message}
        </p>
      ) : null}
    </section>
  );
}
